import type { CSSProperties, ReactNode } from 'react';
import {
  buildMarkdownBlocks,
  splitTextByUrls,
  tokenizeInline,
  type InlineToken,
  type MarkdownBlock,
} from '@org/utils-client';
import { ExternalLink } from 'lucide-react';
import { cn } from '../../utils';
import { ZoomableImage } from './zoomable-image';
import { zineColors, zineFonts } from '../p2e/zine/zine-theme';

const HEX_COLOR_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const BLOCK_MARKDOWN_PATTERN = /^\s{0,3}(?:#{1,6}\s|>{1,3}\s|[-*+]\s|\d+\.\s|```|~~~)/m;
const MULTI_PARAGRAPH_PATTERN = /\n\s*\n/;
const STORAGE_BASE_URL = 'https://storage.googleapis.com';
const DEFAULT_BUCKET_NAME = 'media2empower-latam';
const DEFAULT_IMAGE_EXTENSION = '.png';
const FILE_EXTENSION_PATTERN = /\.[a-z0-9]+$/i;
const ABSOLUTE_URL_PATTERN = /^https?:\/\//i;

type EnvWithBucket = {
  VITE_GCS_BUCKET_NAME?: string;
  GCS_BUCKET_NAME?: string;
  [key: string]: string | undefined;
};

type ImportMetaWithEnv = ImportMeta & { env?: EnvWithBucket };

const ENV: EnvWithBucket =
  ((import.meta as ImportMetaWithEnv).env ?? {}) as EnvWithBucket;
const BUCKET_NAME =
  ENV.VITE_GCS_BUCKET_NAME?.trim() ||
  ENV.GCS_BUCKET_NAME?.trim() ||
  DEFAULT_BUCKET_NAME;

const COLOR_CLASS_MAP: Record<string, string> = {
  red: 'text-red-600',
  rose: 'text-rose-600',
  pink: 'text-pink-600',
  orange: 'text-orange-600',
  amber: 'text-amber-600',
  yellow: 'text-yellow-600',
  green: 'text-emerald-600',
  teal: 'text-teal-600',
  blue: 'text-blue-600',
  sky: 'text-sky-600',
  cyan: 'text-cyan-600',
  indigo: 'text-indigo-600',
  violet: 'text-violet-600',
  purple: 'text-purple-600',
  slate: 'text-slate-700',
  gray: 'text-gray-700',
  grey: 'text-gray-700',
  black: 'text-black',
};

export interface MarkdownRenderClassNames {
  strong?: string;
  italic?: string;
  underline?: string;
  mark?: string;
  color?: string;
  image?: string;
  paragraph?: string;
  heading?: string;
  blockquote?: string;
  blockquoteLine?: string;
  blockquoteGap?: string;
  link?: string;
  text?: string;
  /**
   * How links render. `'inline'` (default) = underlined text link.
   * `'button'` = the lesson "call to action" pill: an authored
   * `[Label](https://…)` shows its label, a bare URL shows `bareLinkLabel`
   * (or the site's domain when no label is given).
   */
  linkVariant?: 'inline' | 'button';
  /** Button text for bare URLs when `linkVariant` is `'button'`. */
  bareLinkLabel?: string;
}

const defaultLinkClass =
  // `overflow-wrap: anywhere` (not `break-word`) so a long bare URL can wrap inside
  // the link box instead of overflowing past the image or card it sits under.
  'inline-flex max-w-full items-center gap-1 font-semibold text-primary tracking-tight transition hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 [overflow-wrap:anywhere]';

/**
 * Lesson link "call to action": a square brand-pink tag (zine charm pink,
 * ink text, condensed caps) so a link in a lesson reads as a bold label of
 * the platform rather than a raw URL.
 */
const linkButtonClass =
  'my-1 inline-flex max-w-full items-center gap-1.5 px-3.5 py-2 align-middle text-[0.82rem] font-semibold uppercase leading-tight no-underline transition-transform duration-150 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D41920]/40 focus-visible:ring-offset-2 [overflow-wrap:anywhere]';

const linkButtonStyle: CSSProperties = {
  backgroundColor: zineColors.charm,
  color: zineColors.ink,
  fontFamily: zineFonts.cond,
  fontStyle: 'normal',
  letterSpacing: '0.06em',
  borderRadius: 0,
  boxShadow: `3px 3px 0 ${zineColors.ink}`,
};

function describeLinkHost(href: string): string {
  try {
    return new URL(href).hostname.replace(/^www\./i, '');
  } catch {
    return href;
  }
}

function renderLink(
  key: string,
  href: string,
  label: string | undefined,
  title: string | undefined,
  classNames?: Pick<MarkdownRenderClassNames, 'link' | 'linkVariant' | 'bareLinkLabel'>,
  /** how a bare URL was written in the text (e.g. without https:// for www.) */
  display?: string,
): ReactNode {
  if (classNames?.linkVariant === 'button') {
    const text = label ?? classNames.bareLinkLabel ?? describeLinkHost(href);
    return (
      <a
        key={key}
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        title={title ?? display ?? href}
        className={cn(linkButtonClass, classNames.link)}
        style={linkButtonStyle}
        data-lesson-link="button"
      >
        <ExternalLink className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{text}</span>
      </a>
    );
  }

  return (
    <a
      key={key}
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      title={title}
      className={cn(defaultLinkClass, label != null && 'underline underline-offset-2', classNames?.link)}
    >
      {label ?? display ?? href}
    </a>
  );
}

export function isLikelyMarkdownContent(text?: string | null): boolean {
  if (!text || !text.trim()) {
    return false;
  }

  return (
    BLOCK_MARKDOWN_PATTERN.test(text) ||
    MULTI_PARAGRAPH_PATTERN.test(text)
  );
}

export function renderPlainTextWithLinks(
  text?: string | null,
  keyPrefix = 'content-text',
  classNames?: Pick<MarkdownRenderClassNames, 'link' | 'text' | 'linkVariant' | 'bareLinkLabel'>,
): ReactNode {
  if (!text) {
    return null;
  }

  const segments = splitTextByUrls(text);
  if (segments.length === 1 && segments[0].type === 'text') {
    return <span className={classNames?.text}>{text}</span>;
  }

  return segments.map((segment, idx) => {
    if (segment.type === 'url') {
      return renderLink(
        `${keyPrefix}-${idx}`,
        segment.href,
        undefined,
        undefined,
        classNames,
        segment.display,
      );
    }

    return (
      <span key={`${keyPrefix}-${idx}`} className={classNames?.text}>
        {segment.content}
      </span>
    );
  });
}

export function renderInlineMarkdown(
  content: string | null | undefined,
  keyPrefix = 'inline-markdown',
  classNames?: MarkdownRenderClassNames,
): ReactNode {
  const lines = (content ?? '').split('\n');
  const renderedLines: ReactNode[] = [];

  lines.forEach((line, lineIndex) => {
    const tokens = tokenizeInline(line);
    const nodes: ReactNode[] =
      tokens.length > 0
        ? tokens.map((token, tokenIndex) =>
            renderInlineToken(
              token,
              `${keyPrefix}-token-${lineIndex}-${tokenIndex}`,
              classNames,
            ),
          )
        : [
            <span key={`${keyPrefix}-token-${lineIndex}-0`} className={classNames?.text}>
              {renderPlainTextWithLinks(line, `${keyPrefix}-line-${lineIndex}`, classNames)}
            </span>,
          ];

    renderedLines.push(...nodes);

    if (lineIndex < lines.length - 1) {
      renderedLines.push(
        <span
          key={`${keyPrefix}-br-${lineIndex}`}
          aria-hidden="true"
          className="block h-0 w-full shrink-0 grow-0 basis-full"
          role="presentation"
        />,
      );
    }
  });

  return renderedLines;
}

export function renderMarkdownBlocks(
  blocks: MarkdownBlock[],
  keyPrefix = 'markdown-block',
  classNames?: MarkdownRenderClassNames,
): ReactNode {
  if (!blocks.length) {
    return null;
  }

  return blocks.map((block, index) => {
    const key = `${keyPrefix}-${block.type}-${index}`;
    switch (block.type) {
      case 'heading': {
        const HeadingTag = `h${block.level}` as keyof JSX.IntrinsicElements;
        return (
          <HeadingTag key={key} className={classNames?.heading}>
            {renderInlineMarkdown(block.content, `${key}-heading`, classNames)}
          </HeadingTag>
        );
      }
      case 'blockquote': {
        const lines = block.content.split('\n');
        return (
          <blockquote key={key} className={classNames?.blockquote}>
            {lines.map((line, lineIdx) => {
              const lineKey = `${key}-line-${lineIdx}`;
              if (!line.trim()) {
                return (
                  <div
                    key={lineKey}
                    className={classNames?.blockquoteGap}
                    aria-hidden="true"
                  />
                );
              }

              return (
                <p key={lineKey} className={classNames?.blockquoteLine}>
                  {renderInlineMarkdown(line, `${lineKey}-content`, classNames)}
                </p>
              );
            })}
          </blockquote>
        );
      }
      default:
        return (
          <p key={key} className={classNames?.paragraph}>
            {renderInlineMarkdown(block.content, `${key}-paragraph`, classNames)}
          </p>
        );
    }
  });
}

export function renderMarkdownFromText(
  text: string | null | undefined,
  keyPrefix = 'markdown-text',
  classNames?: MarkdownRenderClassNames,
): ReactNode {
  const blocks = buildMarkdownBlocks(text);
  return renderMarkdownBlocks(blocks, keyPrefix, classNames);
}

function renderInlineToken(
  token: InlineToken,
  tokenKey: string,
  classNames?: MarkdownRenderClassNames,
): ReactNode {
  switch (token.type) {
    case 'bold':
      return (
        <strong key={tokenKey} className={classNames?.strong}>
          {renderNestedContent(token.content, `${tokenKey}-bold`, classNames)}
        </strong>
      );
    case 'italic':
      return (
        <em key={tokenKey} className={classNames?.italic}>
          {renderNestedContent(token.content, `${tokenKey}-italic`, classNames)}
        </em>
      );
    case 'underline':
      return (
        <u key={tokenKey} className={classNames?.underline}>
          {renderNestedContent(token.content, `${tokenKey}-underline`, classNames)}
        </u>
      );
    case 'mark':
      return (
        <mark key={tokenKey} className={classNames?.mark}>
          {renderNestedContent(token.content, `${tokenKey}-mark`, classNames)}
        </mark>
      );
    case 'color': {
      const colorPresentation = resolveColorPresentation(token.color);
      return (
        <span
          key={tokenKey}
          className={cn(classNames?.color, colorPresentation?.className)}
          style={colorPresentation?.style}
        >
          {renderNestedContent(token.content, `${tokenKey}-color`, classNames)}
        </span>
      );
    }
    case 'link':
      return renderLink(tokenKey, token.href, token.label, token.title, classNames);
    case 'image': {
      const resolvedSrc = resolveImageSource(token.src);
      if (!resolvedSrc) {
        return null;
      }

      return (
        <ZoomableImage
          key={tokenKey}
          src={resolvedSrc}
          alt={token.alt || token.src}
          title={token.title}
          className={cn(
            'inline-block align-middle',
            classNames?.image,
          )}
          loading="lazy"
          decoding="async"
          style={{ width: 'auto', height: 'auto' }}
        />
      );
    }
    default:
      return (
        <span key={tokenKey} className={classNames?.text}>
          {renderPlainTextWithLinks(token.content, `${tokenKey}-text`, classNames)}
        </span>
      );
  }
}

/**
 * Recursively renders nested inline markdown content.
 * This allows combinations like **%%red critical%%** to render as bold + red.
 */
function renderNestedContent(
  content: string,
  keyPrefix: string,
  classNames?: MarkdownRenderClassNames,
): ReactNode {
  const tokens = tokenizeInline(content);
  
  // If no tokens or only plain text tokens, use plain text with links
  if (tokens.length === 0) {
    return renderPlainTextWithLinks(content, keyPrefix, classNames);
  }
  
  // Check if all tokens are just plain text
  const hasFormatting = tokens.some((t) => t.type !== 'text');
  if (!hasFormatting) {
    return renderPlainTextWithLinks(content, keyPrefix, classNames);
  }
  
  // Recursively render tokens
  return tokens.map((token, index) =>
    renderInlineToken(token, `${keyPrefix}-${index}`, classNames),
  );
}

function resolveColorPresentation(
  rawColor: string,
): { className?: string; style?: CSSProperties } | null {
  if (!rawColor) {
    return null;
  }

  const color = rawColor.trim().toLowerCase();
  if (!color) {
    return null;
  }

  if (COLOR_CLASS_MAP[color]) {
    return { className: COLOR_CLASS_MAP[color] };
  }

  if (HEX_COLOR_PATTERN.test(color)) {
    return { style: { color } };
  }

  return null;
}

function resolveImageSource(rawSource: string): string | null {
  if (!rawSource) {
    return null;
  }

  const trimmed = rawSource.trim();
  if (!trimmed) {
    return null;
  }

  if (ABSOLUTE_URL_PATTERN.test(trimmed)) {
    return trimmed;
  }

  const normalizedPath = trimmed.replace(/^\/+/, '');
  const hasExtension = FILE_EXTENSION_PATTERN.test(normalizedPath);
  const assetKey = hasExtension
    ? normalizedPath
    : `${normalizedPath}${DEFAULT_IMAGE_EXTENSION}`;

  return `${STORAGE_BASE_URL}/${BUCKET_NAME}/public/${assetKey}`;
}

